import { ToolBoxCommand, Slot, SortableTreeSlotServiceCommandManager, SortableTreeSlotServiceCommandType, MouseCaptureInfo } from "./sortableTreeSlot";
import { Helper } from "../util/helper";
import { IntKeyDictionary } from "../../commonInterfaces";
import React from "react";

export class Tree extends React.Component<SortableTreeProps, { dragging?: boolean }> {
    private icuh = Helper;
    //HTML elements
    private containerRef: HTMLDivElement = null;
    private cancelContainerRef: HTMLDivElement = null;
    //tree structure & data
    private data: InternalNode = null;
    private flat: Array<InternalNode> = [];
    //general settings
    private config = {
        padding: 20
    };
    //dragged slot
    private selectionInfo = {
        paddingLeft: 0 as number,
        contentOffsetX: 0 as number,
        selectedNode: null as InternalNode,
        height: 0 as number
    };
    //selected slot
    private selectedContentNodeGuid: string = null;
    private commandData = {
        cutSource: null as InternalNode,
        copySource: null as InternalNode
    };
    //placeholder
    private placeholder = {
        target: null as number,
        //TODO: use location???
        above: false,
        height: 0,
        //location: null as PlaceholderLocation,
        upperNode: null as InternalNode,
        lowerNode: null as InternalNode,
        levels: [] as Array<number>,
        currentLevel: -1,
        dropCandidates: [] as Array<number>
    };
    //cancel
    private shouldCancel = false;
    //commands
    private serviceCommandCallbacks: IntKeyDictionary<Array<(nodeId: string, data?: any) => void>> = {};
    private serviceCommands: SortableTreeSlotServiceCommandManager = {
        register: (type: SortableTreeSlotServiceCommandType, action: (nodeId: string, data?: any) => void) => {
            if (!Array.isArray(this.serviceCommandCallbacks[type])) {
                this.serviceCommandCallbacks[type] = [];
            }

            this.serviceCommandCallbacks[type].push(action);
        },
        deregister: (type: SortableTreeSlotServiceCommandType, action: (nodeId: string, data?: any) => void) => {
            const idx = this.serviceCommandCallbacks[type].indexOf(action);
            if (idx !== -1) {
                this.serviceCommandCallbacks[type].splice(idx, 1);
            }
        }
    };

    constructor(props: SortableTreeProps) {
        super(props);
        this.state = {
            dragging: false,
        }
        this.prepareData();

        //change...
        if (isFinite(props.levelPaddingPixels) && props.levelPaddingPixels > 0) {
            this.config.padding = props.levelPaddingPixels;
        }
    }

    private setContainerRef = (r: HTMLDivElement) => {
        this.containerRef = r;
    }

    private setCancelContainerRef = (r: HTMLDivElement) => {
        this.cancelContainerRef = r;
        if (this.cancelContainerRef) {
            this.cancelContainerRef.addEventListener("mouseenter", () => {
                this.shouldCancel = true;
            });
            this.cancelContainerRef.addEventListener("mouseleave", () => {
                this.shouldCancel = false;
            });
        }
    }

    private onDragStart = (node: InternalNode, height: number, info: { contentOffsetX: number }) => {
        this.placeholder.height = height;
        this.selectionInfo.selectedNode = node;
        this.selectionInfo.contentOffsetX = info.contentOffsetX;

        this.updatePlaceholderData(this.getPreviousLinearNode(node.index, node), false);
        this.setState({ dragging: true });
    }

    private onDragEnd = (node: InternalNode, canceled: boolean) => {
        const after = () => {
            this.resetPlaceholderData();
            this.selectionInfo.paddingLeft = 0;
            this.selectionInfo.contentOffsetX = 0;
            this.selectionInfo.selectedNode = null;
            this.setState({ dragging: false });
        }

        if (this.shouldCancel || canceled) {
            after();
        } else {
            this.checkAndMoveNode(after);
        }
    }

    private canSelectNode = (node: InternalNode) => {
        if (this.props.canBeSelected) {
            return this.props.canBeSelected(node.node);
        } else {
            return Promise.resolve(true);
        }
    }

    private onContentSelected = (node: InternalNode) => {
        this.flat.forEach(n => {
            n.contentSelected = false;
        });
        node.contentSelected = true;
        this.selectedContentNodeGuid = node.guid;

        this.setState({}, () => {
            if (this.icuh.hasValue(this.props.afterSelect)) {
                this.props.afterSelect(node.node);
            }
        });
    }

    private onToggle = (node: InternalNode, isCollapsed: boolean) => {
        ///
    }

    private checkAndMoveNode = (cleaUp: Function) => {
        const changeData = this.getChangeDataInfo();

        if (this.placeholder.levels.length > 0 && changeData.next.parent.canTakeChildren && (changeData.current.parent !== changeData.next.parent || changeData.current.index !== changeData.next.index)) {
            const node = this.selectionInfo.selectedNode;
            const nodeIndex = node.parentNode.children.indexOf(node.node);

            if (this.icuh.hasValue(this.props.isChangeAllowed)) {
                this.props.isChangeAllowed(node.node, changeData.current.parent.node, changeData.current.index, changeData.next.parent.node, changeData.next.index, NodeChangeMode.Move)
                    .then(result => {
                        if (result) {
                            node.parentNode.children.splice(nodeIndex, 1);
                            let indexCorrection = 0;

                            if (changeData.current.parent === changeData.next.parent) {
                                let idx = changeData.next.index;
                                if (nodeIndex < changeData.next.index) {
                                    idx--;
                                    indexCorrection++;
                                }
                                changeData.next.parent.node.children.splice(idx, 0, node.node);
                            } else {
                                if (!Array.isArray(changeData.next.parent.node.children)) {
                                    changeData.next.parent.node.children = [node.node];
                                } else {
                                    changeData.next.parent.node.children.splice(changeData.next.index, 0, node.node);
                                }
                            }
                            if (this.props.afterChange) {
                                this.props.afterChange(node.node, changeData.current.parent.node, changeData.current.index, changeData.next.parent.node, changeData.next.index - indexCorrection, NodeChangeMode.Move);
                            }
                            this.prepareData();
                            this.setState({}, () => { cleaUp(); });
                        } else {
                            cleaUp();
                        }
                    }, () => {
                        console.warn("[SortableTree] Check promise...");
                    });
            } else {
                cleaUp();
            }
        } else {
            cleaUp();
        }
    }

    private renderNodeData = (node: InternalNode) => {
        return this.props.noderRenderer(node.node);
    }

    private convertNode = (flat: Array<InternalNode>, node: SortableTreeNode, parentNode: SortableTreeNode, parent: InternalNode, level: number, childIndex: number): InternalNode => {
        if (!node) {
            return null;
        }

        if (!node.id) {
            node.id = kendo.guid();
        }

        const result: InternalNode = {
            guid: node.id,
            id: parent === null ? "1" : (parent.id + "." + childIndex.toString()),
            index: flat.length,
            level: level,
            node: node,
            parentNode: parentNode,
            parent: parent,
            canTakeChildren: !!node.canHaveChildren,
            collapsed: !!node.collapsed,
            children: null,
            contentSelected: this.selectedContentNodeGuid && this.selectedContentNodeGuid === node.id
        };
        flat.push(result);

        if (Array.isArray(node.children) && node.children.length > 0) {
            result.children = [];
            node.children.forEach((n, i) => {
                result.children.push(this.convertNode(flat, n, node, result, level + 1, i + 1));
            });
        }

        return result;
    }

    private prepareData = () => {
        this.flat.length = 0;
        this.data = this.convertNode(this.flat, this.props.tree, null, null, 0, null);
    }

    private isPartOfSubTree = (node: InternalNode, subtree: InternalNode) => {
        if (node === null || subtree === null) {
            return false;
        }

        if (node === subtree) {
            return true;
        }

        let n = node;
        let p = node.parent;
        let found = false;
        while (!found && p) {
            if (p === subtree) {
                found = true;
            } else {
                n = p;
                p = n.parent;
            }
        }

        return found;
    }

    private isHidden = (node: InternalNode) => {
        if (node === null) {
            return false;
        } else {
            if (node.parent) {
                if (node.parent.collapsed) {
                    return true;
                } else {
                    return this.isHidden(node.parent);
                }
            } else {
                return false;
            }
        }
    }

    private moveUp = (node: InternalNode) => {
        if (node.parent && node.parent.canTakeChildren) {
            const currentIdx = node.parent.children.indexOf(node);
            if (currentIdx > 0) {
                if (!this.icuh.hasValue(this.props.isChangeAllowed) || this.props.isChangeAllowed(node.node, node.parent.node, currentIdx, node.parent.node, currentIdx - 1, NodeChangeMode.Move)) {
                    node.parent.node.children.splice(currentIdx, 1);
                    node.parent.node.children.splice(currentIdx - 1, 0, node.node);
                    if (this.props.afterChange) {
                        this.props.afterChange(node.node, node.parent.node, currentIdx, node.parent.node, currentIdx - 1, NodeChangeMode.Move);
                    }
                    this.prepareData();
                    this.setState({});
                }
            }
        }
    }

    private moveDown = (node: InternalNode) => {
        if (node.parent && node.parent.canTakeChildren) {
            const currentIdx = node.parent.children.indexOf(node);
            if (currentIdx < node.parent.children.length - 1) {
                if (!this.icuh.hasValue(this.props.isChangeAllowed) || this.props.isChangeAllowed(node.node, node.parent.node, currentIdx, node.parent.node, currentIdx + 1, NodeChangeMode.Move)) {
                    node.parent.node.children.splice(currentIdx, 1);
                    node.parent.node.children.splice(currentIdx + 1, 0, node.node);
                    if (this.props.afterChange) {
                        this.props.afterChange(node.node, node.parent.node, currentIdx, node.parent.node, currentIdx + 1, NodeChangeMode.Move);
                    }
                    this.prepareData();
                    this.setState({});
                }
            }
        }
    }

    private cut = (node: InternalNode) => {
        this.commandData.cutSource = node;
        this.setState({});
    }

    private copy = (node: InternalNode) => {
        this.commandData.copySource = node;
        this.setState({});
    }

    private append = (node: InternalNode) => {
        if (node.canTakeChildren && (!!this.commandData.copySource || this.commandData.cutSource)) {

            if (this.commandData.cutSource) {
                const idx = this.commandData.cutSource.parent.children.indexOf(this.commandData.cutSource);
                const nextIdx = Array.isArray(node.node.children) ? node.node.children.length : 0;

                if (!this.icuh.hasValue(this.props.isChangeAllowed) || this.props.isChangeAllowed(this.commandData.cutSource.node, this.commandData.cutSource.parent.node, idx, node.node, nextIdx, NodeChangeMode.Move)) {
                    if (!Array.isArray(node.node.children)) {
                        node.node.children = [];
                    }

                    this.commandData.cutSource.parent.node.children.splice(idx, 1);
                    if (!Array.isArray(node.node.children)) {
                        node.node.children = [];
                    }
                    node.node.children.push(this.commandData.cutSource.node);

                    if (this.props.afterChange) {
                        this.props.afterChange(this.commandData.cutSource.node, this.commandData.cutSource.parent.node, idx, node.node, nextIdx, NodeChangeMode.Move);
                    }

                    this.resetCommandData();
                    this.prepareData();
                    this.setState({});
                }
            } else {
                const idx = this.commandData.copySource.parent.children.indexOf(this.commandData.copySource);
                const nextIdx = Array.isArray(node.node.children) ? node.node.children.length : 0;

                if (!this.icuh.hasValue(this.props.isChangeAllowed) || this.props.isChangeAllowed(this.commandData.copySource.node, this.commandData.copySource.parent.node, idx, (node.parent ? node.parent.node : null), nextIdx, NodeChangeMode.Copy)) {
                    if (!Array.isArray(node.node.children)) {
                        node.node.children = [];
                    }
                    //copy
                    if (this.icuh.hasValue(this.props.nodeParenProperty)) {
                        const copy: SortableTreeNode = JSON.parse(JSON.stringify(this.commandData.copySource.node, (key, value) => {
                            if (key === "_id") {
                                return kendo.guid();
                            } else {
                                return key === this.props.nodeParenProperty ? null : value
                            }
                        }));
                        copy.id = kendo.guid();

                        const setParent = (n: SortableTreeNode) => {
                            if (Array.isArray(n.children)) {
                                n.children.forEach(c => {
                                    c[this.props.nodeParenProperty] = n;
                                    setParent(c);
                                });
                            }
                        }

                        copy[this.props.nodeParenProperty] = node.node;
                        setParent(copy);

                        node.node.children.push(copy);
                    } else {
                        const copy: SortableTreeNode = JSON.parse(JSON.stringify(this.commandData.copySource.node));
                        copy.id = kendo.guid();
                        node.node.children.push(copy);
                    }

                    if (this.props.afterChange) {
                        this.props.afterChange(this.commandData.copySource.node, this.commandData.copySource.parent.node, idx, (node.node ? node.node : null), nextIdx, NodeChangeMode.Copy);
                    }

                    this.resetCommandData();
                    this.prepareData();
                    this.setState({});
                }
            }
        }
    }

    private delete = (node: InternalNode) => {
        if (node.parent) {
            const idx = node.parent.node.children.indexOf(node.node)
            if (this.props.isChangeAllowed(node.node, node.parent.node, idx, null, null, NodeChangeMode.Delete)) {
                node.parent.node.children.splice(idx, 1);
                if (this.props.afterChange) {
                    this.props.afterChange(node.node, node.parent.node, idx, null, null, NodeChangeMode.Delete);
                }

                this.resetCommandData();
                this.prepareData();
                this.setState({});
                Helper.hasValue(this.props.afterDelete)
                    this.props.afterDelete(node);
            }
        }
    }

    private onCommand = (node: InternalNode, cmd: ToolBoxCommand, data: Object) => {
        switch (cmd) {
            case ToolBoxCommand.Up:
                this.moveUp(node);
                break;
            case ToolBoxCommand.Down:
                this.moveDown(node);
                break;
            case ToolBoxCommand.Cut:
                this.cut(node);
                break;
            case ToolBoxCommand.Copy:
                this.copy(node);
                break;
            case ToolBoxCommand.Append:
                this.append(node);
                break;
            case ToolBoxCommand.Delete:
                this.delete(node);
                break;
            default:
                break;
        }
    }

    private resetPlaceholderData = () => {
        this.placeholder.target = null;
        this.placeholder.above = false;
        this.placeholder.height = 0;
        //this._placeholder.location = null;
        this.placeholder.upperNode = null;
        this.placeholder.lowerNode = null;
        this.placeholder.levels.length = 0;
        this.placeholder.currentLevel = -1;
        this.placeholder.dropCandidates.length = 0;
    }

    private resetCommandData = () => {
        this.commandData.copySource = null;
        this.commandData.cutSource = null;
    }

    private getPreviousLinearNode = (index: number, rejectionSubTree: InternalNode) => {
        let target: InternalNode = null;
        let idx = null;
        for (let i = 0; i < this.flat.length; i++) {
            if (this.flat[i].index === index) {
                target = this.flat[i];
                idx = i;
                break;
            }
        }

        if (!target || target.index <= 0) {
            return null;
        } else {
            let candidate = this.flat[idx - 1];

        //    if (rejectionSubTree) {
                //if the candidate node is part of the subtree to reject (e.g. the selected subtree)...
            //    if (candidate !== rejectionSubTree && this.isPartOfSubTree(candidate, rejectionSubTree)) {
                    //...go up until the root of the subtree is found
                    while (candidate !== null && candidate !== rejectionSubTree) {
                        candidate = candidate.parent;
                    }
            //    }

                //could be null...
                if (candidate === rejectionSubTree) {
                    //reached the root of the whole tree
                    //there's no "previous" node...
                    if (candidate.index === 0) {
                        return null;
                    } else {
                        //move to the previous node
                        candidate = this.flat[candidate.index - 1];
                    }
                }
          //  }

            while (this.isHidden(candidate) && candidate.index > 0) {
                candidate = this.flat[candidate.index - 1];
            }

            return candidate
        }
    }

    private getNextLinearNode = (index: number, rejectionSubTree: InternalNode) => {
        let target: InternalNode = null;
        let idx = null;
        //find the specified node
        for (let i = 0; i < this.flat.length; i++) {
            if (this.flat[i].index === index) {
                target = this.flat[i];
                idx = i;
                break;
            }
        }

        if (!target) {
            //the node could not be found
            return null;
        } else {
            //if the target node is the last in the list
            if (idx === this.flat.length) {
                return null;
            }

            //advance to the next node inde list
            let candidate = this.flat[idx + 1];

            //find the next node which is not part of a collapsed subtree
            while (candidate && this.isHidden(candidate) /*&& candidate.index < this._flat.length*/) {
                candidate = this.flat[candidate.index + 1];
            }

            //might have reached the end of the tree
            if (!candidate) {
                return null;
            }

            if (rejectionSubTree) {
                //if the candidate (lower) node is the rejection tree root...
                if (candidate === rejectionSubTree) {
                    //...check the following nodes until a node is found, which is not contained in the rejection tree
                    //or there are no more nodes in the list
                    while (candidate && this.isPartOfSubTree(candidate, rejectionSubTree)) {
                        candidate = this.flat[candidate.index + 1];
                    }
                }

                //if no candidate was found
                if (!candidate) {
                    return null;
                }
            }

            return candidate;
        }
    }

    private getChangeDataInfo = () => {
        const changeData = {
            current: {
                parent: null as InternalNode,
                index: null as number
            },
            next: {
                parent: null as InternalNode,
                index: null as number
            }
        };
        changeData.current.parent = this.selectionInfo.selectedNode.parent;
        changeData.current.index = changeData.current.parent.children.indexOf(this.selectionInfo.selectedNode);

        if (this.placeholder.upperNode && this.placeholder.currentLevel === this.placeholder.upperNode.level + 1) {
            // => first child of the upper node
            changeData.next.parent = this.placeholder.upperNode;
            changeData.next.index = 0;
        } else if (this.placeholder.upperNode && this.placeholder.currentLevel === this.placeholder.upperNode.level) {
            // => add after the upper node, on the same level
            changeData.next.parent = this.placeholder.upperNode.parent;
            changeData.next.index = this.placeholder.upperNode.parent.children.indexOf(this.placeholder.upperNode) + 1;
        } else if (!this.placeholder.upperNode && this.placeholder.lowerNode && this.placeholder.lowerNode.level === 0) {
            // => lower node is the root...
            changeData.next.parent = this.placeholder.lowerNode;
            changeData.next.index = 0;
        } else if (this.placeholder.lowerNode && this.placeholder.currentLevel === this.placeholder.lowerNode.level) {
            // => insert before the lower node, on the same level
            changeData.next.parent = this.placeholder.lowerNode.parent;
            changeData.next.index = this.placeholder.lowerNode.parent.children.indexOf(this.placeholder.lowerNode);
        } else {
            let node = this.placeholder.upperNode;
            while (node && node.level !== this.placeholder.currentLevel) {
                node = node.parent;
            }

            if (node && node.parent) {
                // => insert before the lower node, on the selected level
                changeData.next.parent = node.parent;
                changeData.next.index = node.parent.children.indexOf(node) + 1;
            } else {
                console.log("review...");
            }
        }

        return changeData;
    }

    private getRootInfoProvider = () => {
        const rect = this.containerRef.getBoundingClientRect();
        return {
            mouse: {
                distanceToTop: rect.top,
                distanceToLeft: rect.left
            }
        };
    }

    private updatePlaceholderData = (node: InternalNode, upperHalf: boolean) => {
        const height = this.placeholder.height;
        this.resetPlaceholderData();

        //this._placeholder.target = node.index;
        this.placeholder.above = upperHalf;
        //this._placeholder.location = upperHalf ? PlaceholderLocation.AboveTarget : PlaceholderLocation.BelowTarget;
        this.placeholder.height = height;

        let otherNode = upperHalf ?
            this.getPreviousLinearNode(node.index, this.selectionInfo.selectedNode)
            : this.getNextLinearNode(node.index, this.selectionInfo.selectedNode);

        //higher in the hierachy ("closer" to root)
        const upperNode = upperHalf ? otherNode : node;
        //lower in the hierachy, next "unselected" node
        const lowerNode = upperHalf ? node : otherNode;

        this.placeholder.upperNode = upperNode;
        this.placeholder.lowerNode = lowerNode;
        this.placeholder.target = upperNode === null ? lowerNode.index : upperNode.index;

        if (upperNode) {
            if (upperNode.canTakeChildren && !upperNode.collapsed) {
                this.placeholder.levels.push(upperNode.level + 1);
                this.placeholder.dropCandidates.push(upperNode.index);
            }

            if (lowerNode === null) {
                let p = upperNode;
                while (p.parent) {
                    if (p.parent.canTakeChildren) {
                        this.placeholder.levels.unshift(p.level);
                        this.placeholder.dropCandidates.push(p.index);
                    }
                    p = p.parent;
                }
            } else if (Array.isArray(upperNode.children) && (upperNode.children[0] === lowerNode || (upperNode.children[0] === this.selectionInfo.selectedNode && upperNode.children.length > 1))) {
                //... nothing...
            } else {
                let p = upperNode;
                let done = false;
                while (!done && p) {
                    if (p.parent && p.parent.canTakeChildren) {
                        this.placeholder.levels.unshift(p.level);
                        this.placeholder.dropCandidates.push(p.index);
                    }

                    if (p.parent &&
                        ((p.parent.children[p.parent.children.length - 1] === p)
                            || (p.parent.children[p.parent.children.length - 2] === p && p.parent.children[p.parent.children.length - 1] === this.selectionInfo.selectedNode))) {
                        p = p.parent;
                    } else {
                        done = true;
                    }
                }
            }
        } else if (lowerNode && lowerNode.level === 0 && lowerNode.canTakeChildren) {
            //root
            this.placeholder.levels.push(0);
        }


        if (this.placeholder.levels.length > 0) {
            if (this.placeholder.levels.indexOf(this.selectionInfo.selectedNode.level) >= 0) {
                this.placeholder.currentLevel = this.selectionInfo.selectedNode.level;
            } else {
                this.placeholder.currentLevel = this.placeholder.levels[this.placeholder.levels.length - 1];
            }
        }
    }

    private onMouseCapture = (node: InternalNode, data: MouseCaptureInfo) => {
        //only after a slot has been selected
        //ignore "exit events" (it doesn't flicker...)
        if (this.selectionInfo.selectedNode && data.hasMouse) {
            if (node.index < 0) {
                if (isFinite(data.level) && data.level !== null && data.level !== this.placeholder.currentLevel) {
                    this.placeholder.currentLevel = data.level;
                    this.setState({});
                }
            } else {
                if (node.index !== this.placeholder.target || data.upperHalf !== this.placeholder.above) {
                    const prevTarget = this.placeholder.target;
                    this.updatePlaceholderData(node, data.upperHalf);

                    if (prevTarget !== this.placeholder.target) {
                        this.setState({});
                    }

                }
            }
        }
    }

    public refresh = (afterRefresh?: () => void) => {
        this.prepareData();
        this.resetCommandData();
        this.resetPlaceholderData();
        this.setState({}, afterRefresh);
    }

    public select = (node: SortableTreeNode) => {
        let selected: InternalNode = null;
        let next: InternalNode = null;
        this.flat.forEach(n => {
            if (n.node === node) {
                next = n;
            }
            if (n.guid === this.selectedContentNodeGuid) {
                selected = n;
            }
        });
        if (next) {
            if (Array.isArray(this.serviceCommandCallbacks[SortableTreeSlotServiceCommandType.Select])) {
                this.serviceCommandCallbacks[SortableTreeSlotServiceCommandType.Select].forEach(f => f(next.guid));
            }
        }
    }

    render() {
        const classes: Array<string> = ["sortable-tree__wrapper"];
        if (this.state.dragging) {
            classes.push("sortable-tree__wrapper--dragging")
        }
        return (
            <div className={classes.join(" ")} ref={this.setContainerRef} style={{ display: "flex" }} >
                <div className="sortable-tree__cancel-area" style={{ width: "20px", paddingTop: "30px" }} ref={this.setCancelContainerRef}>
                    {
                        this.state.dragging ? <div style={{ transform: "rotate(90deg) translateY(-4px)", transformOrigin: "center top" }}>CANCEL</div> : null
                    }
                </div>
                <div style={{ flex: 1 }}>
                    <Slot
                        index={0}
                        level={0}
                        isPlaceholder={false}
                        node={this.data}
                        selection={{
                            contentOffsetX: this.selectionInfo.contentOffsetX
                        }}
                        onDragStart={this.onDragStart}
                        onDragEnd={this.onDragEnd}
                        contentRenderer={this.renderNodeData}
                        placeholderData={{
                            dropHeight: this.placeholder.height,
                            //location: this._placeholder.location,
                            target: this.placeholder.target,
                            levels: this.placeholder.levels.slice(),
                            currentLevel: this.placeholder.currentLevel,
                            dropCandidates: this.placeholder.dropCandidates
                        }}
                        onMouseCapture={this.onMouseCapture}
                        onContentSelected={this.onContentSelected}
                        onToggle={this.onToggle}
                        onCommand={this.onCommand}
                        commandData={{
                            copyTarget: this.commandData.copySource ? this.commandData.copySource.index : null,
                            cutTarget: this.commandData.cutSource ? this.commandData.cutSource.index : null
                        }}
                        levelPaddingPixels={this.config.padding}
                        commands={this.props.commands}
                        canBeSelected={this.canSelectNode}
                        rootInfoProvider={this.getRootInfoProvider}
                        rightContentRenderer={this.props.rightContentRenderer}
                        serviceCommandManager={this.serviceCommands}
                        canNotDrag={this.props.canNotDrag}
                    />
                </div>
            </div>
        );
    }
}

export interface SortableTreeProps {
    tree: SortableTreeNode;
    isChangeAllowed?: (node: SortableTreeNode, parent: SortableTreeNode, index: number, targetParent: SortableTreeNode, targetIndex: number, mode: NodeChangeMode) => Promise<boolean>;
    afterChange?: (node: SortableTreeNode, previousParent: SortableTreeNode, previousIndex: number, currentParent: SortableTreeNode, currentIndex: number, mode: NodeChangeMode) => void;
    canBeSelected?: (node: SortableTreeNode) => Promise<boolean>;
    afterSelect?: (node: SortableTreeNode) => void;
    afterDelete?: (node: SortableTreeNode) => void;
    noderRenderer: (node: SortableTreeNode) => JSX.Element;
    rightContentRenderer?: (node: SortableTreeNode) => JSX.Element;
    levelPaddingPixels?: number;
    commands?: SortableTreeNodeCommands;
    nodeParenProperty?: string;
    canNotDrag?: boolean;
}

export enum NodeChangeMode {
    Move = 0, Copy = 1, Delete = 2
}

export interface SortableTreeNode {
    id?: string;
    //change will not be "seen"
    canHaveChildren?: boolean;
    children?: Array<SortableTreeNode>;
    //change will not be "seen"
    collapsed?: boolean;
}

export interface InternalNode {
    guid: string;
    id: string;
    index: number;
    level: number;
    node: SortableTreeNode;
    parentNode: SortableTreeNode;
    parent: InternalNode,
    canTakeChildren: boolean;
    collapsed: boolean;
    children: Array<InternalNode>;
    contentSelected: boolean;
}

export interface SortableTreeNodeCommands {
    canShow: (type: ToolBoxCommand, node: SortableTreeNode, name?: string) => boolean,
    custom?: Array<{
        name: string;
        iconName: string;
        hint?: string;
        onClick: (command: string, node: SortableTreeNode) => void;
    }>;
}